// Turns the uploaded house.png into reusable cutouts for the exterior theme.
// The PNG is a haunted house (center, amber-lit windows + a door), a grey moon
// (top-right) and a bare tree (bottom-left) on a flat grey background. We:
//   - sample the corner as the grey key,
//   - find the three elements as connected foreground components,
//   - chroma-key the house + tree to transparent, circular-clip the moon,
//   - auto-detect the amber window/door rects inside the house crop.
// Everything is best-effort: any failure returns null and the caller falls back
// to the procedural exterior, so the game never breaks on a bad/missing asset.

export interface ArtWindow {
  x: number; // house-canvas local pixels
  y: number;
  w: number;
  h: number;
  kind: "window" | "door";
}

export interface ExteriorArt {
  house: HTMLCanvasElement;
  moon: HTMLCanvasElement;
  tree: HTMLCanvasElement;
  windows: ArtWindow[]; // in house-canvas local pixels
}

interface Comp {
  id: number; // its label in the returned `labels` array
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  area: number;
  cx: number;
  cy: number;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed: " + url));
    img.src = url;
  });
}

// 4-connected components over a foreground predicate; returns each component's
// bbox/area/centroid/id plus the per-pixel `labels` array (for exact masking).
function components(W: number, H: number, isFg: (x: number, y: number) => boolean, minArea: number): { comps: Comp[]; labels: Int32Array } {
  const lab = new Int32Array(W * H);
  const out: Comp[] = [];
  const stack: number[] = [];
  let next = 1;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (lab[y * W + x] || !isFg(x, y)) continue;
      const id = next++;
      lab[y * W + x] = id;
      stack.length = 0;
      stack.push(x, y);
      let minX = x;
      let minY = y;
      let maxX = x;
      let maxY = y;
      let area = 0;
      let sx = 0;
      let sy = 0;
      while (stack.length) {
        const py = stack.pop() as number;
        const px = stack.pop() as number;
        area++;
        sx += px;
        sy += py;
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;
        const nb = [
          [px - 1, py],
          [px + 1, py],
          [px, py - 1],
          [px, py + 1],
        ];
        for (const [nx, ny] of nb) {
          if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
          if (lab[ny * W + nx] || !isFg(nx, ny)) continue;
          lab[ny * W + nx] = id;
          stack.push(nx, ny);
        }
      }
      if (area >= minArea) out.push({ id, minX, minY, maxX, maxY, area, cx: sx / area, cy: sy / area });
    }
  }
  return { comps: out, labels: lab };
}

function makeCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("2d context unavailable");
  ctx.imageSmoothingEnabled = false;
  return [c, ctx];
}

export async function processHouseArt(url: string): Promise<ExteriorArt | null> {
  try {
    const img = await loadImage(url);
    const W = img.naturalWidth;
    const H = img.naturalHeight;
    if (!W || !H) return null;

    const [, sctx] = makeCanvas(W, H);
    sctx.drawImage(img, 0, 0);
    const src = sctx.canvas;
    const data = sctx.getImageData(0, 0, W, H).data;

    const bg = [data[0], data[1], data[2]];
    const KEY2 = 46 * 46; // grey-distance² threshold
    const isBg = (x: number, y: number) => {
      const i = (y * W + x) * 4;
      const dr = data[i] - bg[0];
      const dg = data[i + 1] - bg[1];
      const db = data[i + 2] - bg[2];
      return dr * dr + dg * dg + db * db < KEY2;
    };

    const { comps, labels } = components(W, H, (x, y) => !isBg(x, y), 200);
    if (comps.length < 1) return null;
    comps.sort((a, b) => b.area - a.area);

    const house = comps[0]; // largest mass
    const moon = comps.find((c) => c !== house && c.cx > W * 0.58 && c.cy < H * 0.42);
    const tree = comps.find((c) => c !== house && c !== moon && c.cx < W * 0.42 && c.cy > H * 0.5);
    if (!moon || !tree) return null;

    // Crop a component's bbox, keeping ONLY pixels belonging to that component
    // (the house bbox overlaps the moon's x-range, so a chroma-key alone would
    // leave a moon sliver in the house crop → a phantom second moon).
    const cropComp = (c: Comp): HTMLCanvasElement => {
      const w = c.maxX - c.minX + 1;
      const h = c.maxY - c.minY + 1;
      const [cv, cx] = makeCanvas(w, h);
      cx.drawImage(src, c.minX, c.minY, w, h, 0, 0, w, h);
      const im = cx.getImageData(0, 0, w, h);
      const d = im.data;
      for (let yy = 0; yy < h; yy++) {
        for (let xx = 0; xx < w; xx++) {
          if (labels[(c.minY + yy) * W + (c.minX + xx)] !== c.id) d[(yy * w + xx) * 4 + 3] = 0;
        }
      }
      cx.putImageData(im, 0, 0);
      return cv;
    };

    const houseCanvas = cropComp(house);
    const windows = detectWindows(houseCanvas);
    if (windows.length < 4) return null; // detection clearly failed -> fall back

    return { house: houseCanvas, moon: cropComp(moon), tree: cropComp(tree), windows };
  } catch {
    return null;
  }
}

// Find the lit amber windows + door inside the (transparent-bg) house crop.
// Amber panes fragment heavily (mullions, arch tops, the double door splits in
// two), so the raw components are merged — any two within MERGE_GAP px are
// unioned into one whole window — before becoming slots.
const MERGE_GAP = 8;

function detectWindows(houseCanvas: HTMLCanvasElement): ArtWindow[] {
  const hw = houseCanvas.width;
  const hh = houseCanvas.height;
  const hctx = houseCanvas.getContext("2d", { willReadFrequently: true });
  if (!hctx) return [];
  const d = hctx.getImageData(0, 0, hw, hh).data;
  const isAmber = (x: number, y: number) => {
    const i = (y * hw + x) * 4;
    const r = d[i];
    const g = d[i + 1];
    const b = d[i + 2];
    const a = d[i + 3];
    return a > 40 && r > 150 && g > 90 && b < 130 && r > b + 45; // warm/bright glow
  };
  const frags = components(hw, hh, isAmber, 24).comps;
  if (!frags.length) return [];

  // merge fragments whose (gap-expanded) boxes overlap, iterating to a fixpoint
  let boxes = frags.map((c) => ({ x0: c.minX, y0: c.minY, x1: c.maxX, y1: c.maxY }));
  for (let changed = true; changed; ) {
    changed = false;
    const out: typeof boxes = [];
    for (const b of boxes) {
      let placed = false;
      for (const o of out) {
        if (!(b.x0 > o.x1 + MERGE_GAP || b.x1 < o.x0 - MERGE_GAP || b.y0 > o.y1 + MERGE_GAP || b.y1 < o.y0 - MERGE_GAP)) {
          o.x0 = Math.min(o.x0, b.x0);
          o.y0 = Math.min(o.y0, b.y0);
          o.x1 = Math.max(o.x1, b.x1);
          o.y1 = Math.max(o.y1, b.y1);
          placed = true;
          changed = true;
          break;
        }
      }
      if (!placed) out.push({ ...b });
    }
    boxes = out;
  }

  const wins = boxes
    .map((b) => ({ x: b.x0, y: b.y0, w: b.x1 - b.x0 + 1, h: b.y1 - b.y0 + 1 }))
    .filter((b) => b.w > 10 && b.h > 12);
  if (!wins.length) return [];

  // the door is the tallest merged cluster (clearly taller, near the centre)
  let maxH = 0;
  for (const w of wins) if (w.h > maxH) maxH = w.h;
  return wins.map((w) => ({
    ...w,
    kind: w.h === maxH && maxH > 70 && Math.abs(w.x + w.w / 2 - hw / 2) < hw * 0.22 ? "door" : "window",
  }));
}
